/**
 * A tela de entrada do escritório — a folha de boas-vindas do Tahoe.
 *
 * É a mesma anatomia da entrada do Atlas (e do Flux): uma janela de raio 20
 * sobre o papel de parede, dividida em duas — o formulário à esquerda, a faixa
 * da marca à direita, pintada com o `--accent` do sistema. Toda a geometria
 * mora em `estilo/entrada.css`, vindo da fonte única da frota.
 *
 * ⚠️ SÓ EXISTE QUANDO HÁ SERVIDOR. Sem `VITE_SUPABASE_URL` configurada, esta
 * tela nunca aparece e o sistema abre direto na carteira — que é como a
 * Gráfica Boa União começa, com um computador e o banco do próprio navegador.
 *
 * ⚠️ E ELA NÃO BLOQUEIA O TRABALHO. Quem não entra continua com o sistema
 * local inteiro: importa OS, vê a carteira, aponta. O que a entrada liga é a
 * SINCRONIZAÇÃO — enxergar o que os outros aparelhos fizeram, e mandar o que
 * este fez.
 */

import { h, render, sf, type Filho } from '@pcpgbu/ui';
import type { Aplicacao } from './estado.js';

export function telaDeEntrada(app: Aplicacao, destino: HTMLElement, aoEntrar: () => void): void {
  let ocupado = false;

  const campoEmail = h('input', {
    type: 'email',
    placeholder: 'voce@pcpgbu.centriu.com',
    autocomplete: 'username',
    autofocus: true,
  }) as HTMLInputElement;

  const campoSenha = h('input', {
    type: 'password',
    placeholder: '••••••••',
    autocomplete: 'current-password',
  }) as HTMLInputElement;

  const erro = h('.tahoe-aviso.erro', { hidden: true }, h('p'));

  const alternarVerSenha = (): void => {
    const visivel = campoSenha.type === 'text';
    campoSenha.type = visivel ? 'password' : 'text';
    olho.replaceChildren(sf(visivel ? 'eye' : 'eye-slash'));
  };
  const olho = h('button', {
    type: 'button',
    title: 'Mostrar senha',
    'aria-label': 'Mostrar senha',
    onclick: alternarVerSenha,
  }, sf('eye'));

  const entrar = async (): Promise<void> => {
    if (ocupado) return;
    erro.hidden = true;

    const email = campoEmail.value.trim();
    const senha = campoSenha.value;
    if (!email || !senha) {
      (erro.lastElementChild ?? erro).textContent = 'Preencha o e-mail e a senha.';
      erro.hidden = false;
      return;
    }

    ocupado = true;
    botaoEntrar.setAttribute('disabled', '');
    botaoEntrar.replaceChildren(h('span.mac-spinner', { style: { color: '#fff' } }), 'Entrando…');

    try {
      await app.nuvem.entrar(email, senha);
      aoEntrar();
    } catch (e) {
      (erro.lastElementChild ?? erro).textContent = (e as Error).message;
      erro.hidden = false;
      campoSenha.value = '';
      campoSenha.focus();
    } finally {
      ocupado = false;
      botaoEntrar.removeAttribute('disabled');
      botaoEntrar.replaceChildren(h('span', 'Entrar'), sf('arrow-right'));
    }
  };

  const botaoEntrar = h('button.tahoe-btn-primaria', {
    type: 'submit',
  }, [h('span', 'Entrar'), sf('arrow-right')]) as HTMLButtonElement;

  const aoTeclar = (ev: KeyboardEvent): void => {
    if (ev.key === 'Enter') void entrar();
  };
  campoEmail.addEventListener('keydown', aoTeclar);
  campoSenha.addEventListener('keydown', aoTeclar);

  const formulario = h('form', {
    onsubmit: (ev: Event) => {
      ev.preventDefault();
      void entrar();
    },
  }, [
    h('h2', 'Entrar'),
    h('p.lead', 'Entre para acompanhar a produção da sua empresa.'),

    erro,

    h('label.campo', [
      h('span.tahoe-rotulo', 'E-mail'),
      h('.tahoe-campo', [sf('envelope'), campoEmail]),
    ]),
    h('label.campo', [
      h('span.tahoe-rotulo', 'Senha'),
      h('.tahoe-campo', [sf('lock'), campoSenha, olho]),
    ]),

    botaoEntrar,
  ]);

  const hero: Filho = [
    h('p.t-caps', 'Controle de produção'),
    h('div', [
      h('h2', 'Da ordem de serviço à produção, sem digitar nada.'),
      h('p', {
        style: { marginTop: '14px' },
      }, 'O sistema lê as ordens de serviço que o ERP da empresa já gera e mostra onde está cada trabalho, com o tempo previsto contra o real de cada etapa.'),
      h('div.vstack.gap-3', { style: { marginTop: '26px' } }, [
        linhaHero('doc-on-doc', 'Lê as OS do sistema atual, sozinho'),
        linhaHero('lock', 'Operador entra por PIN, no tablet da máquina'),
        linhaHero('clock', 'Previsto × real por etapa, estação e operador'),
      ]),
    ]),
    h('p.pe', 'Planejado × produzido, etapa por etapa.'),
  ];

  render(destino, [
    h('.tahoe-entrada', [
      h('.mac-wallpaper', { 'aria-hidden': 'true' }),
      h('.tahoe-entrada-janela', [
        h('.tahoe-entrada-form', [
          h('.row', { style: { gap: '12px' } }, [
            h('span.mac-app-icon.lg', sf('list-bullet')),
            h('div', [
              h('p.marca-nome', { style: { fontSize: '17px' } }, 'PCP'),
              h('p.marca-sub', 'Acompanhamento de produção'),
            ]),
          ]),

          formulario,

          h('.tahoe-nota', { style: { marginTop: '22px' } }, [
            sf('shield'),
            h('div', [
              h('b', 'Acesso seguro'),
              h('span', 'Quem aponta produção não entra por aqui: usa o tablet da máquina, com PIN.'),
            ]),
          ]),
        ]),

        h('.tahoe-entrada-hero', hero),
      ]),
    ]),
  ]);

  campoEmail.focus();
}

function linhaHero(icone: string, texto: string): HTMLElement {
  return h('.row', { style: { gap: '12px', color: 'rgba(255,255,255,.92)' } }, [
    h('span', {
      style: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: '32px', height: '32px', flex: 'none', borderRadius: '50%',
        background: 'rgba(255,255,255,.15)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.25)',
      },
    }, sf(icone)),
    h('span', { style: { fontSize: '13px' } }, texto),
  ]);
}
